import { useEffect, useState } from 'react';
import { Input } from '@/components/ui';

export interface ProductKeywordsFieldProps {
  /** 폼에 저장된 키워드 배열 (정본). */
  value: ReadonlyArray<string> | null | undefined;
  disabled?: boolean;
  /** blur 시 정규화된 배열로 커밋. 빈 배열이면 [] 전달. */
  onChange: (keywords: string[]) => void;
  placeholder?: string;
  /** 키워드 1개 최대 글자 수. 넘는 키워드를 입력란 아래에 짚어 준다(이관 데이터에 긴 키워드가 있음). */
  maxLength?: number;
}

/** 쉼표(,)로 구분된 검색 키워드/태그 입력. 입력 중에는 원문 버퍼를 유지하고(쉼표·공백 자유),
 *  blur 시점에만 trim/dedupe 정규화해 배열로 커밋한다. (매 keystroke 재분할로 타이핑이 끊기던 문제 회피.) */
export const ProductKeywordsField = ({
  value,
  disabled,
  onChange,
  placeholder,
  maxLength,
}: ProductKeywordsFieldProps) => {
  const tooLong = maxLength ? (value ?? []).filter((k) => k.length > maxLength) : [];
  const joined = (value ?? []).join(', ');
  const [buffer, setBuffer] = useState(joined);

  // 외부 값이 바뀌면(편집 진입 reload 등) 버퍼 동기화 — 단, 사용자 입력 중 덮어쓰지 않도록
  // 정규화 결과가 동일할 때는 버퍼를 건드리지 않는다.
  useEffect(() => {
    const normalizedBuffer = buffer
      .split(',')
      .map((s) => s.trim())
      .filter((s) => s.length > 0)
      .join(', ');
    if (normalizedBuffer !== joined) {
      setBuffer(joined);
    }
    // joined 만 의존 — buffer 변화로 재동기화하지 않는다.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [joined]);

  const commit = () => {
    const parts = Array.from(
      new Set(
        buffer
          .split(',')
          .map((s) => s.trim())
          .filter((s) => s.length > 0),
      ),
    );
    onChange(parts);
    setBuffer(parts.join(', '));
  };

  return (
    <div className="flex flex-col gap-1">
      <Input
        value={buffer}
        disabled={disabled}
        placeholder={placeholder}
        onChange={(e) => setBuffer(e.target.value)}
        onBlur={commit}
        aria-invalid={tooLong.length > 0 || undefined}
      />
      {tooLong.length > 0 ? (
        <p role="alert" className="text-xs" style={{ color: 'var(--status-danger-fg)' }} data-testid="keyword-too-long">
          {maxLength}자를 넘는 키워드가 있어 저장할 수 없습니다 — 줄이거나 지워 주세요:{' '}
          {tooLong.map((k) => `"${k}"(${k.length}자)`).join(', ')}
        </p>
      ) : null}
    </div>
  );
};
